import {
  Button,
  cn,
  Dialog,
  DialogBody,
  DialogContent,
  Select,
  Table,
  Tag,
  toast,
  Typography,
} from "@suraasa/placebo-ui"
import {
  keepPreviousData,
  useMutation,
  useQuery,
  useQueryClient,
} from "@tanstack/react-query"
import api from "api"
import { Document, DocumentTag } from "api/resources/documents/types"
import { APIError } from "api/utils"
import Navbar from "components/Navbar"
import PageContainer from "components/PageContainer"
import TableWrapper from "components/TableWrapper"
import NoDataCard from "features/Assignments/NoDataCard"
import { useState } from "react"
import { Navigate } from "react-router-dom"
import { canAccessDocuments } from "utils/documentsAccess"
import useSearchField from "utils/hooks/useSearchField"
import { routes } from "utils/routes"

import DocumentDialog from "./DocumentDialog"

// EQA uses a Professor token too, so hades can't hide the actions there: the frontend must
const CAN_MANAGE = import.meta.env.VITE_EQA_ONLY_MODE !== "true"

const DeleteDialog = ({
  document,
  onClose,
}: {
  document: Document | null
  onClose: () => void
}) => {
  const queryClient = useQueryClient()
  const { mutate, isPending } = useMutation({
    mutationFn: (doc: Document) =>
      api.documents.remove({ urlParams: { documentId: doc.uuid } }),
    onSuccess: () => toast.success("Document deleted"),
    onError: e =>
      toast.error(
        (e instanceof APIError && e.errors.message) ||
          (e instanceof APIError && e.statusCode === 404
            ? "This document no longer exists"
            : "Something went wrong. Please try again later.")
      ),
    // 204, 403 and 404 all mean the list should be reloaded
    onSettled: () => {
      queryClient.invalidateQueries({ queryKey: ["documents"] })
      onClose()
    },
  })

  return (
    <Dialog open={!!document} onOpenChange={o => !o && onClose()}>
      <DialogContent>
        <DialogBody>
          <Typography variant="title3" className="mb-1">
            Delete document?
          </Typography>
          <Typography>
            <strong>{document?.name}</strong> will be removed for every
            professor and EQA user. This can&apos;t be undone.
          </Typography>
          <div className="mt-2 flex justify-end gap-1">
            <Button variant="text" onClick={onClose}>
              Cancel
            </Button>
            <Button
              color="critical"
              loading={isPending}
              onClick={() => document && mutate(document)}
            >
              Delete
            </Button>
          </div>
        </DialogBody>
      </DialogContent>
    </Dialog>
  )
}

const Documents = () => {
  // Typing the URL shouldn't get round the hidden tab
  if (!canAccessDocuments()) return <Navigate to={routes.root} replace />
  return <DocumentsPage />
}

const DocumentsPage = () => {
  const [uploadOpen, setUploadOpen] = useState(false)
  const [editing, setEditing] = useState<Document | null>(null)
  const [deleting, setDeleting] = useState<Document | null>(null)

  const [tagFilter, setTagFilter] = useState<DocumentTag[]>([])
  const { SearchField, searchText } = useSearchField({
    placeholder: "Search by document name",
  })
  const tagValues = tagFilter.map(t => t.value)
  const isFiltered = tagValues.length > 0 || searchText.trim().length > 0

  const tagsQuery = useQuery({
    queryKey: ["document-tags"],
    queryFn: () => api.documents.tags(),
  })

  // Starts with "documents" so mutations' invalidation refreshes every filtered list
  const { data, isLoading, isError } = useQuery({
    queryKey: ["documents", tagValues, searchText.trim()],
    queryFn: () => api.documents.list({ tags: tagValues, search: searchText }),
    placeholderData: keepPreviousData,
  })

  const documents = data ?? []
  const showActions = CAN_MANAGE && documents.some(d => d.canEdit)

  return (
    <div className="min-h-screen pb-4">
      <Navbar hideBackButton />
      <PageContainer>
        <div className="flex flex-wrap items-center justify-between gap-1">
          <Typography variant="title2">Documents</Typography>
          {CAN_MANAGE && (
            <Button onClick={() => setUploadOpen(true)}>
              Upload documents
            </Button>
          )}
        </div>

        <div className="mt-2 flex flex-col gap-1 sm:flex-row">
          <div className="sm:w-1/2">{SearchField}</div>
          <div className="sm:w-1/2">
            <Select<DocumentTag, true>
              isMulti
              options={tagsQuery.data ?? []}
              isLoading={tagsQuery.isLoading}
              value={tagFilter}
              onChange={v => setTagFilter([...v])}
              getOptionLabel={t => t.label}
              getOptionValue={t => t.value}
              placeholder="Filter by tags"
            />
          </div>
        </div>

        {isError ? (
          <NoDataCard
            className="mt-2"
            message="Couldn't load documents. Please try again later."
          />
        ) : (
          <TableWrapper
            initialLoading={isLoading}
            showNoData={false}
            content={className => (
              <Table
                className={cn(className, "mt-2")}
                data={documents}
                headers={[
                  {
                    title: "File name",
                    id: "name",
                    columnComponent: ({ row }) => (
                      <td>
                        {/* The link may expire, so it always comes from this page's fetch */}
                        <a
                          href={row.file}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="text-primary-500 hover:underline"
                        >
                          <Typography>{row.name}</Typography>
                        </a>
                      </td>
                    ),
                  },
                  {
                    title: "Tags",
                    id: "tags",
                    columnComponent: ({ row }) => (
                      <td>
                        <div className="flex flex-wrap gap-0.5 py-1">
                          {row.tags.map(tag => (
                            <Tag key={tag.value}>{tag.label}</Tag>
                          ))}
                        </div>
                      </td>
                    ),
                  },
                  ...(showActions
                    ? [
                        {
                          title: "",
                          id: "actions",
                          align: "right" as const,
                          columnComponent: ({ row }: { row: Document }) => (
                            <td align="right">
                              {row.canEdit && (
                                <div className="flex justify-end gap-0.5">
                                  <Button
                                    variant="text"
                                    size="sm"
                                    onClick={() => setEditing(row)}
                                  >
                                    Edit
                                  </Button>
                                  <Button
                                    variant="text"
                                    size="sm"
                                    color="critical"
                                    onClick={() => setDeleting(row)}
                                  >
                                    Delete
                                  </Button>
                                </div>
                              )}
                            </td>
                          ),
                        },
                      ]
                    : []),
                ]}
                multiSort={false}
              />
            )}
          />
        )}

        {!isLoading && !isError && documents.length === 0 && (
          <NoDataCard
            message={
              isFiltered
                ? "No documents match these filters."
                : "No documents yet."
            }
          />
        )}
      </PageContainer>

      {CAN_MANAGE && (
        <>
          {/* Keyed so the form resets for each new upload or edited document */}
          {uploadOpen && (
            <DocumentDialog open onClose={() => setUploadOpen(false)} />
          )}
          {editing && (
            <DocumentDialog
              key={editing.uuid}
              open
              document={editing}
              onClose={() => setEditing(null)}
            />
          )}
          <DeleteDialog document={deleting} onClose={() => setDeleting(null)} />
        </>
      )}
    </div>
  )
}

export default Documents
